import styles from './Legend.module.css';
import { InfoPanel } from '..';
import { Typography } from '../../components/Shadcn/Typography';
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '../../components/Shadcn/Tooltip/Tooltip';
import { topRightToast } from '../../components/Shadcn/Sonner';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useEffect, useRef } from 'react';
import { choroplethModeState, cifTypeState, rootProps, selAggLayerState, selectedCategoricalLayerState, SingleLegend, userCurrencyState } from '../../states';
import { formatNumberWithUnit, useMapHelpers } from '../../helpers';
import equal from 'fast-deep-equal';
import { GeoId } from '../../types/geo.types';

interface LegendProps {
    data: any;
    classname?: string;
    legendShimmer: boolean;
    geoCode: GeoId;
    mapColorRootProps: rootProps
}

export const Legend: React.FC<LegendProps> = ({ data, classname, legendShimmer, geoCode, mapColorRootProps }) => {
    const selCurrency = useRecoilValue(userCurrencyState);
    const cifType = useRecoilValue(cifTypeState);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const choroplethMode = useRecoilValue(choroplethModeState);
    const { getCurrencyWithSymbol } = useMapHelpers();
    const selMetrics = JSON.parse(localStorage?.getItem("selMetrics") || '{}');
    const [selectedCategoricalLayer] = useRecoilState(selectedCategoricalLayerState)
    const prevCategoricalLengthRef = useRef<number>(0)
    useEffect(() => {
        const len = data?.categoricallegendColors?.length ?? 0
        if (len > 8 && prevCategoricalLengthRef.current !== len) {
            topRightToast.info(
                "Top 9 categories shown in choropleth.",
                "Top categories are calculated on the basis of most number of records across the dataset and are not limited to the focus region. All remaining categories are clubbed as 'other'."
            )
        }
        prevCategoricalLengthRef.current = len
    }, [data?.categoricallegendColors])

    const metric_type = (!selMetrics || Object.values(selMetrics).filter((item: any) => !!item).length === 0)
        ? cifType.map((item: any) => ({ key: item, ismetric: false }))
        : Object.values(selMetrics)
            .map((metric: any) => {
                const { title, ...rest } = metric || {};
                return { ...rest };
            }) || [];

    const isGeoChanged = (geoCode != mapColorRootProps?.geoId) || !equal(metric_type, mapColorRootProps?.metric_type);

    const hasSelectedLayer = cifType?.length > 0 && selAggLayer !== null;

    const hasLegendData = hasSelectedLayer && (choroplethMode === 'categorical'
        ? data?.categoricallegendColors?.length > 0
        : (data?.legendColors?.length > 0 || data?.legendLabels?.length > 0));

    return (
        <>
            {legendShimmer && isGeoChanged ?
                <div className='w-full relative mt-[var(--semantic-xs)] h-[4vh]'>
                    <div className="flex justify-start items-center gap-2 absolute top-0 left-0 w-full h-full">
                        <svg className={`animate-spin h-4 w-4 shrink-0 ${styles.legend_loader}`} viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.25" />
                            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                        <Typography variant="b3" color="solid/neutral/12">
                            loading...
                        </Typography>
                    </div>
                </div> :

                (
                    <div className={`flex flex-col gap-2 bg-white p-0 pr-[var(--semantic-md)] pt-[var(--semantic-2xs)] rounded w-full ${styles.legend_font_style} ${classname}`}>
                        {hasLegendData &&
                            <div className="flex">
                                <div className="w-1/2 flex gap-2 justify-start items-center">
                                    <div className={`${styles.legend_swatch} ${styles.filtered_region_legend}`} />
                                    <Typography variant="b3" className="text-left">
                                        Filtered Region
                                    </Typography>
                                    {/* <InfoPanel classname='fs-14 icon-gray-0' text='The region has been filtered out by your preferences' /> */}
                                </div>
                                {hasSelectedLayer &&
                                    <div className="w-1/2 flex gap-2 justify-start items-center">
                                        <div className={`${styles.legend_swatch} ${styles.no_poi_coverage_legend}`} />
                                        <Typography variant="b3" className="text-left">
                                            No PoI Data
                                        </Typography>

                                        {/* <InfoPanel classname='fs-14 icon-gray-0' text='The region does not have coverage across all selected data layers' /> */}
                                    </div>}
                            </div>}
                        {
                            choroplethMode === 'categorical' && data?.categoricallegendColors?.length >0  && <div className="text-xs font-medium my-[var(--semantic-s)]">{data?.categoricalLayerName} </div>
                        }
                        <div className="flex flex-col">
                            {!hasLegendData ? (
                                <Typography variant="b3" color="solid/neutral/12">
                                    No Layers Available
                                </Typography>
                            ) : choroplethMode === 'categorical' ? (
                                <div className="flex flex-col gap-4">
                                    {data?.categoricallegendColors?.map((item: { color: string; name: string }) => (
                                        <div key={item.name} className="flex items-center gap-2">
                                            <div style={{ background: item.color, width: 12, height: 12, borderRadius: 2, flexShrink: 0 }} />
                                            <Typography variant="b3" color="solid/neutral/12" className="p-0 text-left">
                                                {item.name}
                                            </Typography>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <div className={`w-full ${styles.legend} flex overflow-hidden`}>
                                        {data?.legendColors?.map((color: string, i: number) => (
                                            <div key={i} style={{ background: color, flex: 1 }} />
                                        ))}
                                    </div>
                                    <div className='pt-[var(--semantic-xs)]'>
                                        {data?.legendLabels?.map((item: any) => (
                                            <div key={item.legendLabel} className='flex items-center justify-between'>
                                                <Typography variant="b3" size="2xs" className="p-0 text-left w-1/4 text-epic-neutral-11">
                                                    {item.legendLabel === 'CTV' ? getCurrencyWithSymbol(formatNumberWithUnit(item.legendStart), selCurrency) : item.legendStart}
                                                </Typography>
                                                <TooltipProvider>
                                                    <TooltipRoot>
                                                        <TooltipTrigger className="w-1/2 min-w-0 p-0 bg-transparent border-0">
                                                            <Typography variant="b3" size="2xs" className="p-0 text-center truncate text-epic-neutral-11">
                                                                {item.legendLabel}
                                                            </Typography>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {item.legendLabel}
                                                        </TooltipContent>
                                                    </TooltipRoot>
                                                </TooltipProvider>
                                                <Typography variant="b3" size="2xs" className="p-0 text-right w-1/4 text-epic-neutral-11">
                                                    {item.legendLabel === 'CTV' ? getCurrencyWithSymbol(formatNumberWithUnit(item.legendEnd), selCurrency) : item.legendEnd}
                                                </Typography>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>)
            }
        </>
    );
};